import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, BadgeCheck, BookOpen, Palette, GraduationCap } from "lucide-react";
import { Eyebrow, Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERKS = [
  { icon: BookOpen, label: "Interactive storytelling" },
  { icon: BadgeCheck, label: "Team Bailey certificates" },
  { icon: Palette, label: "Activity resources" },
  { icon: GraduationCap, label: "Curriculum aligned" },
];

export default function Schools() {
  const [form, setForm] = useState({ name: "", email: "", organisation: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, { ...form, type: "school-visit" });
      toast.success("Enquiry sent — Damali will be in touch soon.");
      setForm({ name: "", email: "", organisation: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const field = (key, props) => (
    <input
      data-testid={`school-form-${key}`}
      required
      value={form[key]}
      onChange={(e) => setForm({ ...form, [key]: e.target.value })}
      className="w-full rounded-xl border border-sand bg-white/80 px-4 py-3 text-sm text-walnut placeholder:text-walnutmuted focus:outline-none focus:ring-2 focus:ring-terracotta/50 focus:border-terracotta transition"
      {...props}
    />
  );

  return (
    <section id="schools" data-testid="schools-section" className="py-24 sm:py-32 bg-walnut text-cream relative overflow-hidden">
      <div className="absolute w-[36rem] h-[36rem] rounded-full bg-terracotta/20 blur-[130px] -top-32 -right-32 pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-center relative z-10">
        <div>
          <Reveal>
            <Eyebrow className="mb-4 !text-terracotta">For schools & libraries</Eyebrow>
            <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl mb-5">
              Bring Bailey to your school
            </h2>
            <p className="text-sm sm:text-base text-cream/70 leading-relaxed max-w-lg mb-8">
              Inspire your pupils with an immersive author visit from Damali Allison —
              interactive storytelling, creative workshops, and a celebration of reading
              that children remember for years.
            </p>
          </Reveal>
          <div className="grid grid-cols-2 gap-3 mb-10">
            {PERKS.map((p, i) => (
              <Reveal key={p.label} delay={i * 0.08}>
                <div className="flex items-center gap-3 rounded-2xl border border-cream/15 bg-cream/5 px-4 py-3.5">
                  <p.icon size={18} className="text-terracotta shrink-0" />
                  <span className="text-sm font-medium">{p.label}</span>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="rounded-[1.75rem] overflow-hidden border border-cream/15">
              <img
                src="/images/author-reading-classroom.jpg"
                alt="Damali Allison reading to children in a classroom"
                loading="lazy"
                className="w-full aspect-[16/10] object-cover hover:scale-105 transition-transform duration-700"
              />
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <form
            data-testid="school-visit-form"
            onSubmit={submit}
            className="bg-cream text-walnut rounded-[1.75rem] p-7 sm:p-9 shadow-[0_40px_80px_-30px_rgba(0,0,0,0.5)]"
          >
            <h3 className="font-display font-bold text-xl sm:text-2xl tracking-tight mb-1.5">Book a visit</h3>
            <p className="text-sm text-walnutsoft mb-6">Tell us about your school and we'll plan something special.</p>
            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              {field("name", { placeholder: "Your name" })}
              {field("email", { placeholder: "Email address", type: "email" })}
            </div>
            <div className="mb-4">{field("organisation", { placeholder: "School or library name" })}</div>
            <textarea
              data-testid="school-form-message"
              required
              rows={4}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="Year group, rough dates, anything else we should know…"
              className="w-full rounded-xl border border-sand bg-white/80 px-4 py-3 text-sm text-walnut placeholder:text-walnutmuted focus:outline-none focus:ring-2 focus:ring-terracotta/50 focus:border-terracotta transition mb-6 resize-none"
            />
            <button
              data-testid="school-visit-submit"
              type="submit"
              disabled={sending}
              className="group w-full rounded-full bg-terracotta text-cream py-4 font-display font-bold text-sm sm:text-base flex items-center justify-center gap-2 hover:bg-terracottadark transition-colors disabled:opacity-60"
            >
              {sending ? "Sending…" : "Send enquiry"}
              <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
